import { createContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type React from 'react';

import { ErrorBoundary } from '@storebrand-digital/bank-foundation-components';
import { getText } from '@storebrand-digital/fms-translations';

import useApiService from '@api/hooks/useApiService';
import type { RouteName } from '@app/constants/route.constants';
import type { CustomerAccountProperties } from '@app/types/createCustomer';
import ErrorView from '@components/ErrorView';

import { useCustomerAccount } from './useCustomerAccount';

type Customer = CustomerAccountProperties['customer'];
type AmlInfo = Customer['amlInfo'];

interface UpdateCustomerAccountContextInterface {
  updateAmlInfo: (amlInfo: Partial<AmlInfo>, route: RouteName) => void;
  updateCustomer: (customer: Partial<Customer>, route: RouteName) => void;
  setCustomerAccount: (customerAccount: CustomerAccountProperties) => void;
  customerAccount?: CustomerAccountProperties;
}

export const UpdateCustomerAccountContext = createContext<UpdateCustomerAccountContextInterface>({
  setCustomerAccount: () => null,
  updateAmlInfo: () => null,
  updateCustomer: () => null,
});

interface Props {
  children: React.ReactNode;
}

function UpdateCustomerAccountProviderInner({ children }: Props) {
  const [customerAccountState, setCustomerAccountState] = useState<CustomerAccountProperties>();
  const dataInitialized = useRef(false);
  const { data: customerAccount, mutate: mutateCustomerAccount, error: customerAccountError } = useCustomerAccount();
  const { updateCustomerAccount: updateCustomerAccountApi } = useApiService();

  useEffect(() => {
    if (!dataInitialized.current && customerAccount) {
      setCustomerAccountState(customerAccount);
      dataInitialized.current = true;
    }
  }, [customerAccount]);

  const updateCustomer = useCallback(
    (customer: Partial<Customer>, route: RouteName) => {
      if (!customerAccountState) {
        throw new Error('Customer account not found');
      }
      const newData: CustomerAccountProperties = {
        ...customerAccountState,
        customer: {
          currentUIState: route,
          ...customerAccountState.customer,
          ...customer,
        },
      };
      setCustomerAccountState(newData);
      mutateCustomerAccount(newData, false).catch(() => null);
      updateCustomerAccountApi(newData)
        .then((updateResult) => mutateCustomerAccount(updateResult, false))
        .catch(() => null);
    },

    [customerAccountState, mutateCustomerAccount, updateCustomerAccountApi]
  );

  const updateAmlInfo = useCallback(
    (amlInfo: Partial<AmlInfo>, route: RouteName) => {
      if (!customerAccountState) {
        throw new Error('Customer account not found');
      }
      const newData: CustomerAccountProperties = {
        ...customerAccountState,
        customer: {
          currentUIState: route,
          ...customerAccountState.customer,
          amlInfo: {
            ...customerAccountState.customer.amlInfo,
            ...amlInfo,
          },
        },
      };
      setCustomerAccountState(newData);
      mutateCustomerAccount(newData, false).catch(() => null);
      updateCustomerAccountApi(newData)
        .then((updateResult) => mutateCustomerAccount(updateResult, false))
        .catch(() => null);
    },
    [customerAccountState, mutateCustomerAccount, updateCustomerAccountApi]
  );

  const setCustomerAccount = useCallback(
    (newCustomerAccount: CustomerAccountProperties) => {
      setCustomerAccountState(newCustomerAccount);
      mutateCustomerAccount(newCustomerAccount, false).catch(() => null);
    },
    [mutateCustomerAccount]
  );

  if (!customerAccountState && customerAccountError) {
    throw new Error('Customer account fetch failed');
  }

  const value = useMemo(
    () => ({ updateCustomer, updateAmlInfo, customerAccount, setCustomerAccount }),
    [updateCustomer, customerAccount, updateAmlInfo, setCustomerAccount]
  );

  return <UpdateCustomerAccountContext.Provider value={value}>{children}</UpdateCustomerAccountContext.Provider>;
}

export function UpdateCustomerAccountProvider(props: Props) {
  return (
    <ErrorBoundary errorView={<ErrorView heading={{ value: getText('errorPage.header'), level: 1 }} />}>
      <UpdateCustomerAccountProviderInner {...props} />
    </ErrorBoundary>
  );
}
